import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, Chip, CircularProgress, Stack, Typography } from '@mui/material';

const FUNCTIONS_BASE = 'https://us-central1-bb-target-center.cloudfunctions.net';

const STATUS = {
  confirmed: { label: 'Confirmada', color: 'info' },
  pending_payment: { label: 'Pendiente de pago', color: 'warning' },
  completed: { label: 'Completada', color: 'default' },
  cancelled: { label: 'Cancelada', color: 'default' },
  refunded: { label: 'Reembolsada', color: 'secondary' },
  expired: { label: 'Expirada', color: 'default' },
  no_show: { label: 'No Show', color: 'error' },
};

const formatTime12 = (time) => {
  if (!time) return '';
  const [h, m] = time.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:${m} ${ampm}`;
};

// Últimas 10 reservaciones del cliente dentro de su cuenta (Users → usuario),
// pedido 2026-10-07. Usa getUserReservations (email + teléfono, igual que
// "Mis reservaciones" del público) y ordena por fecha de la reserva.
export default function MemberReservationsCard({ member }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const email = (member && member.email) || '';
  const phone = (member && member.phone) || '';

  useEffect(() => {
    if (!email && !phone) { setRows([]); return undefined; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await axios.post(`${FUNCTIONS_BASE}/getUserReservations`, {
          email: email ? String(email).toLowerCase().trim() : null,
          phone: phone || null,
        });
        if (cancelled) return;
        const all = [...(resp.data?.upcoming || []), ...(resp.data?.past || [])]
          .filter((r) => !r.isBlock)
          .sort((a, b) => `${b.date || ''}${b.startTime || ''}`.localeCompare(`${a.date || ''}${a.startTime || ''}`))
          .slice(0, 10);
        setRows(all);
      } catch (e) {
        console.error('member reservations', e);
        if (!cancelled) setRows([]);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [email, phone]);

  if (!member || !member.id) return null;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">Últimas reservaciones</Typography>
          {loading && <CircularProgress size={18} />}
        </Stack>
        {!loading && rows.length === 0 && (
          <Typography variant="body2" color="text.secondary">Este cliente no tiene reservaciones.</Typography>
        )}
        {rows.map((r) => {
          const showed = r.showed && r.status !== 'no_show';
          const meta = STATUS[r.status] || { label: r.status, color: 'default' };
          return (
            <Stack
              key={r.id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              spacing={1}
              onClick={() => navigate(`/dashboard/reservations/details?id=${r.id}`)}
              sx={{ py: 1, borderBottom: '1px dashed', borderColor: 'divider', cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
            >
              <div style={{ minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>
                  {r.date}{r.startTime ? ` · ${formatTime12(r.startTime)}` : ''}{r.endTime ? ` – ${formatTime12(r.endTime)}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                  {r.categoryName || '—'}{r.laneName ? ` · ${r.laneName}` : ''}
                  {r.companionView && r.bookedBy ? ` · reservada por ${r.bookedBy}` : ''}
                  {r.confirmationCode ? ` · ${r.confirmationCode}` : ''}
                </Typography>
              </div>
              <Stack alignItems="flex-end" spacing={0.5}>
                {showed
                  ? <Chip size="small" color="success" label={r.status === 'completed' ? 'Completada · Showed' : 'Showed'} />
                  : <Chip size="small" color={meta.color} label={meta.label} variant="outlined" />}
                {typeof r.amountCents === 'number' && r.amountCents > 0 && (
                  <Typography variant="caption" color="text.secondary">${(r.amountCents / 100).toFixed(2)}</Typography>
                )}
              </Stack>
            </Stack>
          );
        })}
      </CardContent>
    </Card>
  );
}

MemberReservationsCard.propTypes = {
  member: PropTypes.shape({
    id: PropTypes.string,
    email: PropTypes.string,
    phone: PropTypes.string,
  }),
};
